import React, { useState, useEffect, useRef } from "react";
import EmojiPicker from "emoji-picker-react";
import GiphySearchBox from "react-giphy-searchbox";
import "./Notifications.css";
import NotificationEditModal from "./NotificationEditModal";
import { useNavigate } from "react-router-dom";

const Notifications = () => {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [systemPosts, setSystemPosts] = useState([]);
  const [userAlerts, setUserAlerts] = useState([]);
  const [tab, setTab] = useState("system");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [selectedGif, setSelectedGif] = useState(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const fileRef = useRef(null);
  const [hasNewUserAlerts, setHasNewUserAlerts] = useState(false);
  const [hasNewSystemPosts, setHasNewSystemPosts] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editPostId, setEditPostId] = useState(null);
  const [editInitialContent, setEditInitialContent] = useState("");

  // ✅ Local search term for Notifications page
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ Normalize current search
  const lowerSearch = searchTerm.toLowerCase().trim();

  // ✅ System Notices filter (matches text or @username of the poster)
  const filteredSystemPosts = systemPosts.filter((post) => {
    if (!lowerSearch) return true;

    if (lowerSearch.startsWith("@")) {
      const usernameQuery = lowerSearch.slice(1); // remove "@"
      return post.username?.toLowerCase().includes(usernameQuery);
    }

    return (
      post.content.toLowerCase().includes(lowerSearch) ||
      post.username?.toLowerCase().includes(lowerSearch)
    );
  });

  // ✅ User Alerts filter (matches text or actor username/name)
  const filteredUserAlerts = userAlerts.filter((note) => {
    if (!lowerSearch) return true;

    if (lowerSearch.startsWith("@")) {
      const usernameQuery = lowerSearch.slice(1);
      return (
        note.actor_username?.toLowerCase().includes(usernameQuery) ||
        note.actor_name?.toLowerCase().includes(usernameQuery)
      );
    }

    return (
      note.message?.toLowerCase().includes(lowerSearch) ||
      note.actor_username?.toLowerCase().includes(lowerSearch) ||
      note.actor_name?.toLowerCase().includes(lowerSearch)
    );
  });

  useEffect(() => {
    const user = localStorage.getItem("currentUser");
    if (user) {
      const parsed = JSON.parse(user);
      setCurrentUser(parsed);
      fetchSystemPosts(parsed.id);
      fetchUserAlerts(parsed.id);
    }
  }, []);

  const fetchSystemPosts = (userId) => {
    fetch(`${process.env.REACT_APP_API_URL}/notifications/posts`)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch system posts: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setSystemPosts(data);
        if (data.length > 0) {
          const newest = new Date(data[0].createdAt).toISOString();
          localStorage.setItem("latestSystemNotice", newest);
          const lastSeen = localStorage.getItem(`lastSeenSystemNotices_${userId}`);
          setHasNewSystemPosts(!lastSeen || new Date(newest) > new Date(lastSeen));
        }
      })
      .catch((err) => console.error("❌ System fetch error:", err));
  };

  const fetchUserAlerts = (userId) => {
    fetch(`${process.env.REACT_APP_API_URL}/notifications/user/${userId}`)
      .then((res) => res.json())
      .then((data) => {
        setUserAlerts(data);
        if (data.length > 0) {
          const newest = new Date(data[0].createdAt).toISOString();
          localStorage.setItem("latestUserAlert", newest);
          const lastSeen = localStorage.getItem(`lastSeenUserAlerts_${userId}`);
          setHasNewUserAlerts(!lastSeen || new Date(newest) > new Date(lastSeen));
        }
      })
      .catch((err) => console.error("❌ User alert fetch error:", err));
  };

  const handleTabClick = (type) => {
    setTab(type);
    const now = new Date().toISOString();
    const userId = currentUser?.id;
    if (!userId) return;

    if (type === "user") {
      localStorage.setItem("lastSeenUserAlerts", now);
      localStorage.setItem(`lastSeenUserAlerts_${userId}`, now);
      setHasNewUserAlerts(false);
    }

    if (type === "system") {
      localStorage.setItem("lastSeenSystemNotices", now);
      localStorage.setItem(`lastSeenSystemNotices_${userId}`, now);
      setHasNewSystemPosts(false);
    }
  };

  const handlePost = async () => {
    if (!content.trim() && !file && !selectedGif) return alert("Enter content or media.");
    if (![1, 2, 3, 4].includes(currentUser?.group_id)) return alert("Permission denied.");

    const formData = new FormData();
    formData.append("user_id", currentUser.id);
    formData.append("message", content);
    if (file) formData.append("file", file);
    if (selectedGif) formData.append("gif", selectedGif);
    if (editingId) formData.append("id", editingId);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/notifications/posts`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Post failed.");

      setContent("");
      setFile(null);
      setSelectedGif(null);
      setShowGifPicker(false);
      setShowEmojiPicker(false);
      if (fileRef.current) fileRef.current.value = "";
      setEditingId(null);
      fetchSystemPosts(currentUser.id);
    } catch (err) {
      console.error("❌ Error posting system notice:", err);
      alert("Post failed.");
    }
  };

  const handleSaveEdit = async (id, updatedContent) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/notifications/posts/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content: updatedContent }),
      });

      if (!res.ok) throw new Error("Edit failed");

      fetchSystemPosts(currentUser?.id);
      setEditModalVisible(false);
      setEditPostId(null);
      setEditInitialContent("");
    } catch (err) {
      console.error("❌ Edit error:", err);
      alert("Could not update post.");
    }
  };

  const handleEdit = (post) => {
    setEditPostId(post.id);
    setEditInitialContent(post.content);
    setEditModalVisible(true);
  };

  const handleDelete = async (id) => {
    if (!currentUser?.username || !currentUser?.group_id) {
      alert("❌ Unauthorized. Please log in.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this system message?")) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/notifications/posts/${id}?group_id=${currentUser.group_id}`,
        { method: "DELETE" }
      );

      if (!res.ok) throw new Error("Delete failed.");

      alert("✅ Message deleted.");
      setSystemPosts((prev) => prev.filter((msg) => msg.id !== id));
    } catch (err) {
      console.error("❌ Error deleting post:", err);
      alert("❌ Could not delete system message.");
    }
  };

  const renderSystemNotice = (post) => {
    const thumbnail = post.postUser?.thumbnail?.startsWith("http")
      ? post.postUser.thumbnail
      : `${process.env.REACT_APP_API_URL}${post.postUser?.thumbnail || "/images/default-thumbnail.jpg"}`;

    const canEdit =
      currentUser?.group_id === 1 ||
      (currentUser?.id === post.user_id && [2, 3, 4].includes(currentUser?.group_id));

    const canDelete =
      currentUser?.group_id === 1 ||
      (currentUser?.id === post.user_id && [2, 3, 4].includes(currentUser?.group_id));

    return (
      <div key={post.id} className="notification-post">
        <div className="notification-header">
          <div className="notification-header-left">
            <img
              src={thumbnail}
              alt={post.username}
              className="notification-thumbnail"
            />
            <div className="notification-identity">
              <span
                className="notification-realname"
                title={post.postUser?.name || "System"}
              >
                {post.postUser?.name || "System"}
              </span>
              <span
                className="notification-username"
                title={`@${post.username}`}
              >
                @{post.username}
              </span>
            </div>
          </div>
          <span className="notification-date">
            {new Date(post.createdAt).toLocaleString()}
          </span>
        </div>

        <p className="notification-content">{post.content}</p>

        {post.media_url && (
          post.media_url.endsWith(".mp4") ? (
            <video controls className="notification-media">
              <source src={`${process.env.REACT_APP_API_URL}${post.media_url}`} type="video/mp4" />
            </video>
          ) : (
            <img
              src={`${process.env.REACT_APP_API_URL}${post.media_url}`}
              className="notification-media"
              alt="Media"
            />
          )
        )}

        {post.gif_url && (
          <img
            src={post.gif_url}
            className="notification-media"
            alt="GIF"
          />
        )}

        {(canEdit || canDelete) && (
          <div className="notification-actions">
            {canEdit && (
              <button className="edit-btn" onClick={() => handleEdit(post)}>✏️</button>
            )}
            {canDelete && (
              <button className="delete-btn" onClick={() => handleDelete(post.id)}>🗑</button>
            )}
          </div>
        )}
      </div>
    );
  };

  // ✅ UPDATED: thumbnail, name, and @username now open PublicProfile by username
  const renderUserAlert = (note) => (
  <div key={note.id} className="notification-post alert-layout">

    {/* CLICKABLE THUMBNAIL */}
    <img
      src={`${process.env.REACT_APP_API_URL}${note.actor_thumbnail}`}
      alt="actor"
      className="alert-thumbnail"
      onClick={() => navigate(`/profile/${note.actor_username}`)}
      style={{ cursor: "pointer" }}
    />

    <div className="alert-body">

      {/* CLICKABLE NAME + USERNAME */}
      <div className="alert-header">
        <span
          className="alert-name"
          title={note.actor_name || ""}
          onClick={() => navigate(`/profile/${note.actor_username}`)}
          style={{ cursor: "pointer" }}
        >
          {note.actor_name}
        </span>{" "}
        <span
          className="alert-username"
          title={`@${note.actor_username || ""}`}
          onClick={() => navigate(`/profile/${note.actor_username}`)}
          style={{ cursor: "pointer" }}
        >
          @{note.actor_username}
        </span>{" "}
        <span className="alert-date">
          · {new Date(note.createdAt).toLocaleDateString()}
        </span>
      </div>

      <div
        className="alert-line2"
        onClick={() => {
          if ((note.type === "reply" || note.type === "reply_to_reply") && note.post_id) {
            navigate(`/post/${note.post_id}`);
          }
        }}
        style={{
          cursor: (note.type === "reply" || note.type === "reply_to_reply") && note.post_id ? "pointer" : "default",
        }}
        title={
          (note.type === "reply" || note.type === "reply_to_reply") && note.post_id
            ? "Open this post and its replies"
            : undefined
        }
      >
        {note.type === "like" && <>❤️ liked your post</>}
        {note.type === "reply" && <>💬 replied to your post</>}
        {note.type === "reply_to_reply" && <>💬 replied to your reply</>}
        {note.type === "repost" && <>🔁 reposted your post</>}
        {note.type === "follow" && <>➕ started following you</>}
        {note.type === "report_outcome" && <>⚖️ Scoldy moderation decision</>}
      </div>

      <div
        className="alert-line3"
        onClick={() => {
          if ((note.type === "reply" || note.type === "reply_to_reply") && note.post_id) {
            navigate(`/post/${note.post_id}`);
          }
        }}
        style={{
          cursor: (note.type === "reply" || note.type === "reply_to_reply") && note.post_id ? "pointer" : "default",
        }}
        title={
          (note.type === "reply" || note.type === "reply_to_reply") && note.post_id
            ? "Open this post and its replies"
            : undefined
        }
      >
        {note.message}
      </div>
    </div>
  </div>
);


  return (
    <div className="notifications-page">
      <h1>Notifications</h1>

      <div className="notifications-search">
        <input
          type="text"
          className="notifications-search-box"
          placeholder="Search Notifications or @User"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="notifications-tabs">
        <button onClick={() => handleTabClick("user")} className={tab === "user" ? "active" : ""}>
          🔔 My Notifications {hasNewUserAlerts && <span className="dot-indicator"></span>}
        </button>
        <button onClick={() => handleTabClick("system")} className={tab === "system" ? "active" : ""}>
          📢 System Notices {hasNewSystemPosts && <span className="dot-indicator"></span>}
        </button>
      </div>

      {tab === "system" && [1, 2, 3, 4].includes(currentUser?.group_id) && (
        <div className="notification-post-box">
          <textarea
            placeholder="Post a system notice..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />

          {file && (
            <div style={{ marginTop: "8px", fontSize: "0.9rem" }}>
              📷 {file.name}
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  if (fileRef.current) fileRef.current.value = "";
                }}
                style={{ marginLeft: "8px" }}
              >
                Remove
              </button>
            </div>
          )}

          {selectedGif && (
            <div style={{ marginTop: "8px", textAlign: "center" }}>
              <img
                src={selectedGif}
                alt="Selected GIF"
                style={{ maxWidth: "100%", maxHeight: "180px", borderRadius: "8px" }}
              />
              <button
                type="button"
                onClick={() => setSelectedGif(null)}
                style={{ display: "block", margin: "6px auto 0" }}
              >
                Remove GIF
              </button>
            </div>
          )}

          <div className="notification-action-bar">
            {editingId && (
              <button
                className="cancel-btn"
                onClick={() => {
                  setEditingId(null);
                  setContent("");
                  setFile(null);
                  setSelectedGif(null);
                  setShowGifPicker(false);
                  setShowEmojiPicker(false);
                  if (fileRef.current) fileRef.current.value = "";
                }}
              >
                Cancel
              </button>
            )}

            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="action-btn"
              title="Upload Image or Video"
            >
              📷
            </button>

            <button
              type="button"
              className="action-btn"
              title="Add GIF"
              onClick={() => {
                setShowGifPicker((prev) => !prev);
                setShowEmojiPicker(false);
              }}
            >
              🎞
            </button>

            <button
              type="button"
              className="action-btn"
              title="Insert Emoji"
              onClick={() => {
                setShowEmojiPicker((prev) => !prev);
                setShowGifPicker(false);
              }}
            >
              😊
            </button>

            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const selected = e.target.files?.[0] || null;
                setFile(selected);
                if (selected) setSelectedGif(null);
              }}
            />

            <button className="post-btn" onClick={handlePost}>
              {editingId ? "Save" : "Post"}
            </button>
          </div>

          {showGifPicker && (
            <div style={{ marginTop: "10px", padding: "10px", background: "#fff", border: "1px solid #ddd", borderRadius: "8px" }}>
              <GiphySearchBox
                apiKey="4OQsubW6vV4uAMHoGaiK8VRRavmxEvYW"
                onSelect={(gif) => {
                  const url = gif?.images?.original?.url || null;
                  if (!url) return;
                  setSelectedGif(url);
                  setFile(null);
                  if (fileRef.current) fileRef.current.value = "";
                  setShowGifPicker(false);
                }}
              />
            </div>
          )}

          {showEmojiPicker && (
            <div style={{ marginTop: "10px" }}>
              <EmojiPicker
                onEmojiClick={(emojiData) => {
                  setContent((prev) => prev + (emojiData?.emoji || ""));
                  setShowEmojiPicker(false);
                }}
              />
            </div>
          )}
        </div>
      )}

      <div className="notifications-list">
        {tab === "system"
          ? filteredSystemPosts.length === 0
            ? <p>No system notices.</p>
            : filteredSystemPosts.map(renderSystemNotice)
          : filteredUserAlerts.length === 0
            ? <p>No new alerts.</p>
            : filteredUserAlerts.map(renderUserAlert)}
      </div>

      <NotificationEditModal
        show={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        postId={editPostId}
        userId={currentUser?.id}
        initialContent={editInitialContent}
        onSaveSuccess={() => {
          fetchSystemPosts(currentUser?.id);
          setEditModalVisible(false);
        }}
      />
    </div>
  );
};

export default Notifications;
